import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Users, MessageSquare, Briefcase, Calendar, Network, Heart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    org_name: '',
    org_type: 'nonprofit',
    contact_person: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      onLogin(response.data.access_token, response.data.user);
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, registerData);
      // Store credentials but don't log in yet
      localStorage.setItem('pendingToken', response.data.access_token);
      localStorage.setItem('pendingUser', JSON.stringify(response.data.user));
      toast.success('Account created! Please choose your subscription plan.');
      // Redirect to pricing page
      navigate('/pricing');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-500 rounded-full blur-3xl"></div>
      </div>

      {/* Hero Section */}
      <div className="relative z-10">
        <div className="container mx-auto px-4 py-12">
          {/* Header */}
          <div className="flex justify-center mb-16">
            <img 
              src="https://customer-assets.emergentagent.com/job_bc318d90-16f9-4f9c-83f4-4cb66d5436f1/artifacts/7ff6752k_CEC%20Connect_GraphicLogo-black.png" 
              alt="CEC Connect" 
              className="h-20 w-auto"
              data-testid="cec-logo"
            />
          </div>

          {/* Hero Content */}
          <div className="max-w-4xl mx-auto text-center mb-12 md:mb-16 animate-fade-in px-4">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-4 md:mb-6 leading-tight">
              Connect. Collaborate.
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600 mt-2">
                Create Impact.
              </span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 mb-6 md:mb-8 leading-relaxed max-w-3xl mx-auto">
              The networking platform designed for nonprofits and service organizations to build meaningful connections and drive positive change together.
            </p>
          </div>

          {/* Features Grid */}
          <div className="max-w-6xl mx-auto mb-12 md:mb-20">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100" data-testid="feature-card-directory">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Organization Directory</h3>
                <p className="text-gray-600 text-sm">Discover and connect with nonprofits and service organizations in your area</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100" data-testid="feature-card-messaging">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                  <MessageSquare className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Direct Messaging</h3>
                <p className="text-gray-600 text-sm">Communicate directly with other organizations to build partnerships</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100" data-testid="feature-card-opportunities">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                  <Briefcase className="w-6 h-6 text-teal-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Opportunities Board</h3>
                <p className="text-gray-600 text-sm">Share and discover volunteer opportunities, resources, and partnerships</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100" data-testid="feature-card-events">
                <div className="w-12 h-12 bg-lime-100 rounded-xl flex items-center justify-center mb-4">
                  <Calendar className="w-6 h-6 text-lime-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Event Management</h3>
                <p className="text-gray-600 text-sm">Create and promote events, manage RSVPs, and increase community engagement</p>
              </div>
            </div>
          </div>

          {/* Auth Section */}
          <div className="max-w-md mx-auto animate-slide-down">
            <Card className="shadow-xl border-0">
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl text-center">Get Started</CardTitle>
                <CardDescription className="text-center">
                  Join the community of organizations making a difference
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="login" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                    <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
                  </TabsList>

                  <TabsContent value="login">
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="login-email">Email</Label>
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="your@email.com"
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          required
                          data-testid="login-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="login-password">Password</Label>
                        <Input
                          id="login-password"
                          type="password"
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          required
                          data-testid="login-password-input"
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                        disabled={loading}
                        data-testid="login-submit-button"
                      >
                        {loading ? 'Logging in...' : 'Login'}
                      </Button>
                    </form>
                  </TabsContent>

                  <TabsContent value="register">
                    <form onSubmit={handleRegister} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="register-org">Organization Name</Label>
                        <Input
                          id="register-org"
                          placeholder="Your Organization"
                          value={registerData.org_name}
                          onChange={(e) => setRegisterData({ ...registerData, org_name: e.target.value })}
                          required
                          data-testid="register-org-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Organization Type</Label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="org_type"
                              value="nonprofit"
                              checked={registerData.org_type === 'nonprofit'}
                              onChange={(e) => setRegisterData({ ...registerData, org_type: e.target.value })}
                              className="w-4 h-4 text-green-600"
                              data-testid="org-type-nonprofit"
                            />
                            <span className="text-sm">Nonprofit Organization</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="org_type"
                              value="service"
                              checked={registerData.org_type === 'service'}
                              onChange={(e) => setRegisterData({ ...registerData, org_type: e.target.value })}
                              className="w-4 h-4 text-green-600"
                              data-testid="org-type-service"
                            />
                            <span className="text-sm">Service Organization</span>
                          </label>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="register-contact">Contact Person</Label>
                        <Input
                          id="register-contact"
                          placeholder="John Doe"
                          value={registerData.contact_person}
                          onChange={(e) => setRegisterData({ ...registerData, contact_person: e.target.value })}
                          required
                          data-testid="register-contact-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="register-email">Email</Label>
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="your@email.com"
                          value={registerData.email}
                          onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                          required
                          data-testid="register-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="register-phone">Phone (Optional)</Label>
                        <Input
                          id="register-phone"
                          type="tel"
                          placeholder="(555) 123-4567"
                          value={registerData.phone}
                          onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                          data-testid="register-phone-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="register-password">Password</Label>
                        <Input
                          id="register-password"
                          type="password"
                          placeholder="••••••••"
                          value={registerData.password}
                          onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                          required
                          data-testid="register-password-input"
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                        disabled={loading}
                        data-testid="register-submit-button"
                      >
                        {loading ? 'Creating Account...' : 'Create Account'}
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Footer Image */}
          <div className="mt-20 max-w-2xl mx-auto">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_bc318d90-16f9-4f9c-83f4-4cb66d5436f1/artifacts/cl1tn4h3_Background-small.png" 
                alt="CEC Connect Community" 
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end">
                <div className="p-8 text-white">
                  <h3 className="text-2xl font-semibold mb-2">Growing Together</h3>
                  <p className="text-white/90">Building a network of organizations committed to positive change</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;